import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  DestinationApiResponse, 
  getListaDestination, 
  getDestinationById, 
  createDestination, 
  updateDestination, 
  deleteDestination, 
  getDestinationsPaginated
} from "../../services/destinationService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Destination from "../../types/destination";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";

interface DestinationContextProps {
  apiResponse: ApiResponse<Destination> | null;
  destinations: Destination[] | null;
  destinationById: Destination | null;
  loading: boolean;
  error: string | null;
  fetchDestination: (paginacao: Paginacao | null) => void;
  fetchDestinationById: (id: number) => Promise<Destination | null>;
  postDestination: (destination: Destination) => Promise<DestinationApiResponse | null>;
  putDestination: (id: number, destination: Destination) => Promise<DestinationApiResponse | null>;
  excluiDestination: (id: number) => Promise<boolean>;
}

interface DestinationProviderProps {
  children: React.ReactNode;
}

const DestinationContext = createContext<DestinationContextProps | null>(null);

const DestinationProvider: React.FC<DestinationProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<Destination> | null>(null);
  const [destinations, setDestination] = useState<Destination[] | null>(null);
  const [destinationById, setDestinationById] = useState<Destination | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDestination = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getDestinationsPaginated(paginacao) : await getListaDestination();
      setApiResponse(response);
      setDestination(response.data.values || []);
    } catch (err) {
      setDestination([]);
      setError(getErrorMessage("fetchDestinationError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDestinationById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getDestinationById(id);
      
      setDestinationById(response.data);
      return response.data;
    } catch (err) {
      setError(getErrorMessage("fetchDestinationByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postDestination = useCallback(async (destination: Destination) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createDestination(destination);
      return response;
    } catch (err) {
      setError(getErrorMessage("createDestinationError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const putDestination = useCallback(async (id: number, destination: Destination) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateDestination(id, destination);
      return response;
    } catch (err) {
      setError(getErrorMessage("updateDestinationError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const excluiDestination = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteDestination(id);
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteDestinationError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <DestinationContext.Provider 
      value={{ 
        apiResponse,
        destinations, 
        destinationById, 
        loading, 
        error, 
        fetchDestination, 
        fetchDestinationById, 
        postDestination, 
        putDestination, 
        excluiDestination 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </DestinationContext.Provider>
  );
};

export default DestinationProvider;

export const useDestination = (): DestinationContextProps => {
  const context = useContext(DestinationContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useDestination?.name,
        providerName: DestinationProvider?.name,
      })
    );
  }
  return context;
};
